import React, { memo, type ReactElement, useEffect, useState } from 'react';
import { Box, Link as DSLink, TabContent } from '@saviynt/design-system';
import cx from 'classnames';

import {
  SodMitigatingControls,
  SodSidePanelSummary,
} from '../../../../shared/components';
import {
  TABS_FILTERS,
  VIOLATIONS_DETAILS_TABS,
  VIOLATIONS_LIST_SIDEPANEL_TABS,
  violationsHome,
} from '../../../../shared/constants';
import {
  useInvertedLocalize,
  useNavigateTo,
  useTypedSelector,
} from '../../../../shared/hooks';
import { type ViolationMitigatingControls } from '../../../Details/data-layer/detailsTypes';
import { useHandleMitigatingControl } from '../../../Details/hooks';
import { type ViolationsTabSidePanelRowContextState } from '../../data-layer/violationsTabTypes';

interface ViolationsSidePanelSummaryProps {
  currentRowContext: ViolationsTabSidePanelRowContextState;
  activeTab: string;
  handleChange: (tab: string) => void;
  isMitigatedTab: boolean;
}

/**
 * ViolationsSidePanelSummary
 */
function ViolationsSidePanelSummary({
  currentRowContext,
  activeTab,
  handleChange,
  isMitigatedTab,
}: ViolationsSidePanelSummaryProps): ReactElement {
  const l = useInvertedLocalize();
  const { tabInAction } = useTypedSelector(
    (state) => state.aag.violationsTab.controls
  );

  const { navigateToDetails } = useNavigateTo();
  const { loadMitigateRow, mitigatingControlRows } =
    useHandleMitigatingControl();
  const [controls, setControls] = useState<ViolationMitigatingControls[]>([]);

  useEffect(() => {
    if (isMitigatedTab && currentRowContext.id) {
      const requestData = {
        id: parseInt(currentRowContext.id, 10),
        violationId: currentRowContext.id,
      };

      loadMitigateRow(requestData).catch(() => {});
    }
  }, [isMitigatedTab, currentRowContext.id, loadMitigateRow]);

  useEffect(() => {
    if (mitigatingControlRows.length > 0) {
      setControls(mitigatingControlRows);
    }
  }, [mitigatingControlRows]);

  const summaryTabClasses = cx(
    'Sod-ViolationsSidePanelBody-tabContent',
    'Sod-ViolationsSidePanelBody-tabContent--isSummaryTab'
  );

  const handleMitigatingControlAction = () => {
    navigateToDetails({
      key: currentRowContext.id,
      params: {
        title: `${currentRowContext.title ?? ''}`,
        user: `${currentRowContext.username ?? ''}`,
        type: violationsHome,
        activeTab: VIOLATIONS_DETAILS_TABS.TAB_MITIGATING,
      },
    });
  };

  return (
    <TabContent
      className={summaryTabClasses}
      value={activeTab}
      tabValue={VIOLATIONS_LIST_SIDEPANEL_TABS.TAB_SUMMARY}>
      <Box className='Sod-ViolationsSidePanelBody-tabContentWrapper'>
        <SodSidePanelSummary
          sodKey={currentRowContext.id}
          handleOnUserClick={() => {
            handleChange(VIOLATIONS_LIST_SIDEPANEL_TABS.TAB_USER);
          }}
          handleOnViewHistoryClick={() => {
            handleChange(VIOLATIONS_LIST_SIDEPANEL_TABS.TAB_HISTORY);
          }}
          isInSidePanel
          isClosedTab={tabInAction === TABS_FILTERS.CLOSEDCOUNT.value}
          className='Sod-SummarySidePanel'
          leadingChildren={
            isMitigatedTab && controls.length > 0 ? (
              <SodMitigatingControls
                controls={controls}
                count={controls.length}
                handleLink={() => {
                  navigateToDetails({
                    key: currentRowContext.id,
                    params: {
                      title: `${currentRowContext.title ?? ''}`,
                      user: `${currentRowContext.username ?? ''}`,
                      type: violationsHome,
                      activeTab: VIOLATIONS_DETAILS_TABS.TAB_MITIGATING,
                    },
                  });
                }}
                handleInlineAction={handleMitigatingControlAction}
              />
            ) : null
          }
          trailingChildren={
            <DSLink
              text={l('View Details')}
              color='brand'
              kind='button'
              onClick={() => {
                navigateToDetails({
                  key: currentRowContext.id,
                  params: {
                    title: `${currentRowContext.title ?? ''}`,
                    user: `${currentRowContext.username ?? ''}`,
                    type: violationsHome,
                    activeTab: VIOLATIONS_DETAILS_TABS.TAB_CONFLICTS,
                    username: `${currentRowContext.username ?? ''}`,
                  },
                });
              }}
              suffixIconKind='ArrowRight'
              size='medium'
              className='Sod-ViolationsSidePanelBody-links'
            />
          }
        />
      </Box>
    </TabContent>
  );
}

export default memo(ViolationsSidePanelSummary);
